import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import NavigationContext from './context/NavigationContext';
import StripeElementsProvider from './components/StripeElementsProvider';
import { extractPromoCodeFromUrl, peekPendingPromoCode, savePendingPromoCode } from './utils/providerSubscription';

const AdminStack = React.lazy(() => import('./stack/AdminStack'));
const MainStack = React.lazy(() => import('./stack/MainStack'));
const AuthStack = React.lazy(() => import('./stack/AuthStack'));
const ConsumerStack = React.lazy(() => import('./stack/ConsumerStack'));

const RouteFallback = () => <div>Loading…</div>;

const APP_MODE_KEY = 'appMode';

function App() {
  const [userLoggedIn, setUserLoggedIn] = useState(null);
  const [userRole, setUserRole] = useState('provider');
  const [appMode, setAppModeState] = useState('provider');
  const [loading, setLoading] = useState(true);

  const setAppMode = useCallback((mode) => {
    setAppModeState(mode);
    localStorage.setItem(APP_MODE_KEY, mode);
  }, []);

  useEffect(() => {
    const checkLoginStatus = () => {
      try {
        const promoFromUrl = extractPromoCodeFromUrl(window.location.href);
        if (promoFromUrl) {
          savePendingPromoCode(promoFromUrl);
        }
        const pendingPromo = peekPendingPromoCode();
        const userToken = localStorage.getItem('userToken');
        const storedRole = localStorage.getItem('userRole') || 'provider';
        const storedMode = localStorage.getItem(APP_MODE_KEY) || 'provider';
        if (pendingPromo) {
          localStorage.setItem(APP_MODE_KEY, 'provider');
          setAppModeState('provider');
          if (userToken && userToken.trim() !== '' && storedRole === 'provider') {
            setUserLoggedIn(true);
            setUserRole('provider');
          } else {
            setUserLoggedIn(false);
            setUserRole('provider');
          }
        } else if (userToken && userToken.trim() !== '') {
          setUserLoggedIn(true);
          setUserRole(storedRole);
          // Guest accounts use userRole "user" — keep them in consumer mode on refresh.
          const resolvedMode =
            storedRole === 'user' || storedMode === 'consumer' ? 'consumer' : 'provider';
          setAppModeState(resolvedMode);
        } else {
          setUserLoggedIn(false);
          setUserRole('provider');
          setAppModeState('provider');
        }
      } catch (error) {
        console.error('Error checking login status:', error);
        setUserLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };

    checkLoginStatus();
  }, []);

  const navigationContextValue = {
    userLoggedIn,
    setUserLoggedIn,
    userRole,
    setUserRole,
    appMode,
    setAppMode,
  };

  if (loading) {
    return <div>Loading…</div>;
  }

  const isAdminSession = Boolean(userLoggedIn && userRole === 'admin');
  const isConsumerSession = Boolean(
    userLoggedIn && !isAdminSession && (appMode === 'consumer' || userRole === 'user'),
  );

  return (
    <Router>
      <NavigationContext.Provider value={navigationContextValue}>
        <Suspense fallback={<RouteFallback />}>
          {isAdminSession ? (
            <Routes>
              <Route path="/*" element={<AdminStack />} />
            </Routes>
          ) : isConsumerSession ? (
            <Routes>
              <Route path="/*" element={<ConsumerStack />} />
            </Routes>
          ) : userLoggedIn ? (
            <StripeElementsProvider>
              <Routes>
                <Route path="/*" element={<MainStack />} />
              </Routes>
            </StripeElementsProvider>
          ) : (
            <Routes>
              <Route path="/*" element={<AuthStack />} />
            </Routes>
          )}
        </Suspense>
      </NavigationContext.Provider>
    </Router>
  );
}

export default App;

